import * as Sentry from "@sentry/react";

import { User } from "types/users";

/**
 * Sentry error identifiers for grouping and categorizing errors
 */
export const SENTRY_ERROR_IDENTIFIERS = {
    API_ERROR: "api-error",
    API_TIMEOUT: "api-timeout",
    AUTH_ERROR: "auth-error",
    IMAGE_LOAD_FAILURE: "image-load-failure",
} as const;

/**
 * Sets comprehensive user context in Sentry for error tracking
 * This should be called whenever user information is available or updated
 */
export function setSentryUserContext(user: User | null | undefined): void {
    if (!user || !user.id) {
        Sentry.setUser(null);
        return;
    }

    try {
        // Set core user identification
        Sentry.setUser({
            id: user.id,
            username: user.username,
            email: user.email,
        });

        // Set additional user context as tags for easier filtering in Sentry
        Sentry.setTag("customer_type", user.userprofile?.customer_type || "unknown");
        Sentry.setTag("tier", user.tier);
        Sentry.setTag("auth_flow", user.auth_flow);
        Sentry.setTag("is_superuser", user.is_superuser);
        Sentry.setTag("is_staff", user.is_staff);
        Sentry.setTag("is_labeler", user.is_labeler);
        Sentry.setTag("is_admin_login_view", user.is_admin_login_view);

        // Set account/group information if available
        if (user.groups && user.groups.length > 0) {
            Sentry.setTag("primary_group_id", user.groups[0].id);
            Sentry.setTag("primary_group_name", user.groups[0].name);
            Sentry.setContext("groups", {
                count: user.groups.length,
                groups: user.groups.map(g => ({ id: g.id, name: g.name })),
            });
        }

        // Set user profile context with additional details
        Sentry.setContext("user_profile", {
            name: user.name,
            company: user.userprofile?.company,
            customer_type: user.userprofile?.customer_type,
            account_labeling_settings: user.account_labeling_settings,
            date_joined: user.date_joined,
            accepted_tos: user.accepted_tos,
            explore_detector: user.explore_detector,
            account_members: user.account_members,
        });

        // Set feature flags as context for debugging
        Sentry.setContext("feature_flags", {
            // Customer type based flags
            show_inspections: user.show_inspections,
            show_labelers_management: user.show_labelers_management,
            enable_idp_migration: user.enable_idp_migration,
            can_edit_gl_notes: user.can_edit_gl_notes,
            enable_review_options: user.enable_review_options,
            show_disagreements: user.show_disagreements,
            should_create_disagreements: user.should_create_disagreements,
            allow_explore_selection: user.allow_explore_selection,
            show_full_sources: user.show_full_sources,
            only_labeler_ui: user.only_labeler_ui,
            show_disagreements_management: user.show_disagreements_management,
            // Customer tier based flags
            allow_annotations: user.allow_annotations,
            enable_export_data: user.enable_export_data,
            enable_count_mode: user.enable_count_mode,
            enable_multi_class_mode: user.enable_multi_class_mode,
            // Other flags
            show_reset_detector_option: user.show_reset_detector_option,
            show_system_accuracy: user.show_system_accuracy,
            enable_new_detector_notes_editor: user.enable_new_detector_notes_editor,
            enable_data_page_multiselect_filter: user.enable_data_page_multiselect_filter,
            enable_text_mode: user.enable_text_mode,
            enable_account_invite: user.enable_account_invite,
            enable_bounding_box_mode: user.enable_bounding_box_mode,
        });
    } catch (error) {
        console.warn("Failed to set Sentry user context:", error);
    }
}

/**
 * Captures an API error in Sentry with detailed context
 */
export function captureApiErrorInSentry(
    response: Response,
    errorMessage: string,
    additionalContext?: Record<string, any>
): void {
    try {
        const requestId = response.headers.get("x-request-id-resolved");

        Sentry.withScope((scope) => {
            // Set error level based on status code
            if (response.status >= 500) {
                scope.setLevel("error");
            } else if (response.status >= 400) {
                scope.setLevel("warning");
            }

            // Set API error context
            scope.setContext("api_error", {
                url: response.url,
                status: response.status,
                statusText: response.statusText,
                requestId,
                errorMessage,
                type: "API",
            });

            // Set request headers if available
            const headers: Record<string, string> = {};
            response.headers.forEach((value, key) => {
                headers[key] = value;
            });
            scope.setContext("response_headers", headers);

            // Add any additional context provided
            if (additionalContext) {
                scope.setContext("additional_context", additionalContext);
            }

            scope.setFingerprint([
                SENTRY_ERROR_IDENTIFIERS.API_ERROR,
                response.url,
                String(response.status),
            ]);

            Sentry.captureException(
                new Error(`API Error: ${response.status} ${response.statusText} - ${errorMessage}`),
                {
                    tags: {
                        error_type: SENTRY_ERROR_IDENTIFIERS.API_ERROR,
                        status_code: response.status,
                        request_id: requestId || "unknown",
                    },
                }
            );
        });
    } catch (error) {
        console.warn("Failed to capture error in Sentry:", error);
    }
}

/**
 * Captures a timeout error in Sentry with retry context
 */
export function captureTimeoutErrorInSentry(
    response: Response,
    errorMessage: string,
    attempt: number,
    maxRetries: number
): void {
    try {
        const requestId = response.headers.get("x-request-id-resolved");

        Sentry.withScope((scope) => {
            scope.setLevel("error");

            // Set timeout error context
            scope.setContext("timeout_error", {
                url: response.url,
                status: response.status,
                statusText: response.statusText,
                requestId,
                errorMessage,
                attempt,
                maxRetries,
                type: "API_TIMEOUT",
            });

            scope.setFingerprint([
                SENTRY_ERROR_IDENTIFIERS.API_TIMEOUT,
                response.url,
            ]);

            Sentry.captureException(
                new Error(`API Timeout after ${attempt} retries: ${response.url}`),
                {
                    tags: {
                        error_type: SENTRY_ERROR_IDENTIFIERS.API_TIMEOUT,
                        status_code: response.status,
                        request_id: requestId || "unknown",
                        retry_attempt: attempt,
                    },
                }
            );
        });
    } catch (error) {
        console.warn("Failed to capture timeout error in Sentry:", error);
    }
}

/**
 * Captures authentication errors in Sentry
 */
export function captureAuthErrorInSentry(
    response: Response,
    redirectUrl: string
): void {
    try {
        const requestId = response.headers.get("x-request-id-resolved");

        Sentry.withScope((scope) => {
            scope.setLevel("warning");

            scope.setContext("auth_error", {
                url: response.url,
                status: response.status,
                statusText: response.statusText,
                requestId,
                redirectUrl,
                type: "AUTHENTICATION",
            });

            scope.setFingerprint([
                SENTRY_ERROR_IDENTIFIERS.AUTH_ERROR,
                String(response.status),
            ]);

            Sentry.captureException(
                new Error(`Authentication Error: ${response.status} - Session expired or unauthorized`),
                {
                    tags: {
                        error_type: SENTRY_ERROR_IDENTIFIERS.AUTH_ERROR,
                        status_code: response.status,
                        request_id: requestId || "unknown",
                    },
                }
            );
        });
    } catch (error) {
        console.warn("Failed to capture auth error in Sentry:", error);
    }
}

/**
 * Captures an image load failure in Sentry.
 * This is useful for tracking when images fail to load on pages like the Review Page.
 * The current page context (e.g., "review") will be included via existing Sentry tags.
 */
export function captureImageLoadFailure(
    imageUrl: string,
    component?: string
): void {
    try {
        Sentry.withScope((scope) => {
            scope.setLevel("error");

            scope.setContext("image_load_failure", {
                image_url: imageUrl,
                component: component ?? "unknown",
                type: "IMAGE_LOAD_FAILURE",
            });

            scope.setFingerprint([
                SENTRY_ERROR_IDENTIFIERS.IMAGE_LOAD_FAILURE,
                component ?? "unknown",
            ]);

            Sentry.captureMessage(`Image failed to load: ${imageUrl}`, {
                tags: {
                    error_type: SENTRY_ERROR_IDENTIFIERS.IMAGE_LOAD_FAILURE,
                    component: component ?? "unknown",
                },
            });
        });
    } catch (error) {
        console.warn("Failed to capture image load failure in Sentry:", error);
    }
}
